import { useCallback, useRef } from 'react';
import { ApolloError } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import {
  useContinueContractRemindersMutation,
  usePauseContractRemindersMutation,
} from 'core/graphql';
import { GlobalToastTypeNames, ToastTypes } from 'types';

import { leadCardTargets, useQuietRefetch } from '../Crm/leadRefetch';
import { useToasts } from '../Toast/useToasts';
import { settleOutreachCache } from './useContractOutreach';

type RawErrors =
  | ReadonlyArray<{
      field?: string | null;
      code?: string | null;
      message?: string | null;
    } | null>
  | null
  | undefined;

/**
 * ТЗ 0090 — лесенка подписи договора из карточки ЛИДА (и окна «Поставить на
 * лесенку» в карточке договора): «Остановить» и «Возобновить» по id
 * договора.
 *
 * Те же мутации, что у напоминаний в окне договора
 * (`PauseContractReminders` / `ContinueContractReminders`), и тот же приём с
 * кэшем: ответ — полное состояние напоминаний — кладётся в запрос окна
 * договора (`settleOutreachCache`), сводка списка договоров сбрасывается.
 * Затем карточка лида перечитывается (строка «Подпись договора №…» — её
 * `signingLadder`), не заставляя действие ждать: сбой перечитывания не
 * превращает удачную остановку в «Не получилось».
 *
 * Права — у сервера: действия доступны тому, кто ведёт договор (продавец,
 * AM, Admin); экран прячет кнопки по `canManage`.
 */
export const useContractLadderActions = (
  leadId: string | null,
  onChanged?: () => void,
) => {
  const { t } = useTranslation();
  const { addToast } = useToasts();
  const refetch = useQuietRefetch();
  const [pause, { loading: stopping }] = usePauseContractRemindersMutation();
  const [
    resumeMut,
    { loading: resuming },
  ] = useContinueContractRemindersMutation();
  // Защёлка в ref: второй клик в том же такте ещё видит `loading=false`.
  const inFlight = useRef(false);

  const notify = useCallback(
    (type: ToastTypes, title: string, text?: string) => {
      addToast({
        data: { type, title, text },
        type: GlobalToastTypeNames.Esign,
      });
    },
    [addToast],
  );

  const errorText = useCallback(
    (raw: RawErrors): string =>
      (raw ?? [])
        .filter((item): item is NonNullable<typeof item> => Boolean(item))
        .map((item) =>
          item.code
            ? t(`esign.outreach.errors.${item.code}`, {
                defaultValue: item.message || item.code,
              })
            : item.message ?? '',
        )
        .filter(Boolean)
        .join('\n'),
    [t],
  );

  const run = useCallback(
    async (
      action: () => Promise<
        | {
            success?: boolean | null;
            customErrors?: RawErrors;
          }
        | null
        | undefined
      >,
      okKey: string,
    ): Promise<boolean> => {
      if (inFlight.current) return false;
      inFlight.current = true;
      try {
        const payload = await action();
        if (!payload?.success) {
          notify(
            ToastTypes.error,
            t('esign.outreach.toast.failed'),
            errorText(payload?.customErrors) || undefined,
          );
          // Отказ «нечего останавливать» — экран отстал: перечитать.
          if (leadId) refetch(leadCardTargets(leadId, ['card']));
          return false;
        }
        notify(ToastTypes.success, t(okKey));
        if (leadId) refetch(leadCardTargets(leadId, ['card']));
        if (onChanged) onChanged();
        return true;
      } catch (caught) {
        const noAccess =
          caught instanceof ApolloError && caught.graphQLErrors.length > 0;
        notify(
          ToastTypes.error,
          t('esign.outreach.toast.failed'),
          t(
            noAccess
              ? 'esign.outreach.errors.NO_ACCESS'
              : 'esign.outreach.errors.NETWORK',
          ),
        );
        return false;
      } finally {
        inFlight.current = false;
      }
    },
    [errorText, leadId, notify, onChanged, refetch, t],
  );

  const stop = useCallback(
    (contractId: string) =>
      run(async () => {
        const result = await pause({
          variables: { contractId },
          update: (cache, { data }) =>
            settleOutreachCache(cache, contractId, data?.stopContractReminders),
        });
        return result.data?.stopContractReminders;
      }, 'esign.outreach.toast.stopped'),
    [pause, run],
  );

  const resume = useCallback(
    (contractId: string) =>
      run(async () => {
        const result = await resumeMut({
          variables: { contractId },
          update: (cache, { data }) =>
            settleOutreachCache(
              cache,
              contractId,
              data?.resumeContractReminders,
            ),
        });
        return result.data?.resumeContractReminders;
      }, 'esign.outreach.toast.resumed'),
    [resumeMut, run],
  );

  return { stop, resume, busy: { stopping, resuming } };
};
